import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { LcsAdvancedMultiSelectStateEventPayloads } from '@lcs/shared/advanced-multi-select';
import { ExtensionOption } from '../../extension-register/models/extension-option.interface';
import { QueueHistoryRegisterQueueFilterController } from '../services/queue-history-register-queue-filter.controller';

@Component({
   encapsulation: ViewEncapsulation.None,
   selector: 'ndt-queue-history-register-queue-filter',
   templateUrl: './queue-history-register-queue-filter.component.html',
   standalone: false,
})
export class QueueHistoryRegisterQueueFilterComponent implements OnChanges {
   @Input() isQueueFilterSortable = false;

   @Input() set queueExtensions(value: ExtensionOption[]) {
      this._queueExtensions = value ?? [];
      this.queueFilterController.setQueueExtensions(this._queueExtensions);
   }

   get queueExtensions(): ExtensionOption[] {
      return this._queueExtensions;
   }

   @Input() selectedQueueExtensionIds: number[] = [];

   @Output() queueExtensionIdsChange = new EventEmitter<number[]>();

   private _queueExtensions: ExtensionOption[] = [];

   private readonly queueFilterController = new QueueHistoryRegisterQueueFilterController((queueExtension) =>
      this.getQueueOptionLabel(queueExtension)
   );

   readonly queueFilterExtensionsControl = this.queueFilterController.queueFilterExtensionsControl;

   readonly queueFilterAllControl = this.queueFilterController.queueFilterAllControl;

   readonly availableQueueExtensions = this.queueFilterController.availableQueueExtensions;

   readonly queueFilterSearchText = this.queueFilterController.queueFilterSearchText;

   readonly queueFilterSelectedItems = this.queueFilterController.queueFilterSelectedItems;

   readonly filteredQueueExtensions = this.queueFilterController.filteredQueueExtensions;

   readonly queueFilterDataSource = this.queueFilterController.queueFilterDataSource;

   readonly queueFilterTriggerConfig = this.queueFilterController.queueFilterTriggerConfig;

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['selectedQueueExtensionIds']) {
         this.queueFilterController.setSelectedQueueExtensionIds(this.selectedQueueExtensionIds);
      }
   }

   onQueueExtensionsChange(queueExtensionIds: number[]): void {
      this.selectedQueueExtensionIds = [...(queueExtensionIds ?? [])];
      this.queueFilterController.setSelectedQueueExtensionIds(this.selectedQueueExtensionIds);
      this.queueExtensionIdsChange.emit(this.selectedQueueExtensionIds);
   }

   onQueueFilterOpen(): void {
      this.queueFilterController.onQueueFilterOpen();
   }

   onQueueFilterSave(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['save']): void {
      this.onQueueExtensionsChange(this.queueFilterController.onQueueFilterSave(e));
   }

   onQueueFilterSearch(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['search']): void {
      this.queueFilterController.onQueueFilterSearch(e);
   }

   onQueueFilterSelectedItemsChange(
      e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['selectedItemsChange']
   ): void {
      this.queueFilterController.onQueueFilterSelectedItemsChange(e);
   }

   onQueueFilterDrop(e: LcsAdvancedMultiSelectStateEventPayloads<ExtensionOption>['drop']): void {
      this.queueFilterController.onQueueFilterDrop(e);
   }

   onQueueFilterSelectedItemsDrop(
      event: CdkDragDrop<ExtensionOption[]>,
      context: { actions: { drop: (previousIndex: number, currentIndex: number) => void } }
   ): void {
      if (event.previousIndex === event.currentIndex) {
         return;
      }

      context.actions.drop(event.previousIndex, event.currentIndex);
   }

   onQueueFilterSelectedItemRemove(
      event: Event,
      context: { actions: { unselectItem: (item: ExtensionOption) => void } },
      queueExtension: ExtensionOption
   ): void {
      event.stopPropagation();
      context.actions.unselectItem(queueExtension);
   }

   isQueueFilterItemSelected(selectedQueueExtensions: ExtensionOption[], queueExtension: ExtensionOption): boolean {
      return selectedQueueExtensions.some((selectedQueueExtension) => {
         return selectedQueueExtension.extensionId === queueExtension.extensionId;
      });
   }

   onQueueFilterClose(): void {
      this.queueFilterController.onQueueFilterClose();
   }

   getQueueOptionLabel(queueExtension: ExtensionOption): string {
      return this.getQueueDisplay(
         queueExtension.number ?? null,
         this.getQueueDisplayName(queueExtension.shortName, queueExtension.name)
      );
   }

   private getQueueDisplay(queueNumber: number | null, queueName: string | null): string {
      if (queueNumber != null && queueName) {
         return `${queueNumber} - ${queueName}`;
      }

      if (queueName) {
         return queueName;
      }

      if (queueNumber != null) {
         return `${queueNumber}`;
      }

      return 'Unnamed Queue';
   }

   private getQueueDisplayName(shortName: string | null | undefined, name: string | null | undefined): string | null {
      return shortName?.trim() || name?.trim() || null;
   }
}
